import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';

import { ApiForm, Field, TextInput, useApiForm } from '../../shared/form';
import { Modal } from '../../shared/ui/modal';
import { folderKeys, renameFolder, type TFolder } from '../../entities/folder';

const schema = z.object({
  name: z.string().trim().min(1, 'Name the folder').max(120, 'At most 120 characters'),
});

type TValues = z.infer<typeof schema>;

interface IFolderRenameProps {
  folder: TFolder;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const FolderRename = ({ folder, open, onOpenChange }: IFolderRenameProps) => {
  const client = useQueryClient();
  const api = useApiForm<TValues>({
    schema,
    defaultValues: { name: folder.name },
    onSubmit: async (values) => {
      await renameFolder(folder.id, values.name);
      // Paths of everything filed below it changed too, so the item lists re-read.
      await Promise.all([
        client.invalidateQueries({ queryKey: folderKeys.list(folder.scope) }),
        client.invalidateQueries({ queryKey: [folder.scope === 'flows' ? 'flows' : 'library'] }),
      ]);
      onOpenChange(false);
    },
  });

  useEffect(() => {
    if (open) api.form.reset({ name: folder.name });
  }, [open, folder.name, api.form]);

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Rename folder"
      submit={{
        label: 'Rename',
        onSubmit: () => void api.submit(),
        waiting: api.form.formState.isSubmitting,
        waitingLabel: 'Renaming',
      }}
    >
      <ApiForm api={api}>
        <Field<TValues> name="name" label="Name">
          <TextInput autoFocus />
        </Field>
        <button type="submit" className="hidden" aria-hidden />
      </ApiForm>
    </Modal>
  );
};
